import { faChartMixedUpCircleCurrency, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation as RecommendationModel, TTDCampaign } from '@feathr/blackbox';
import type { ERecommendationType } from '@feathr/blackbox';
import { CardV2 as Card, CollapseButton, Confetti, Recommendation } from '@feathr/components';

import useCardState from './CampaignRecommendations.useCardState';
import useCollapseAndSort from './CampaignRecommendations.useCollapseAndSort';
import useConfetti from './CampaignRecommendations.useConfetti';
import useDismissRecommendation from './CampaignRecommendations.useDismissRecommendation';
import useModal from './CampaignRecommendations.useModal';
import useSelectedRecommendation from './CampaignRecommendations.useSelectedRecommendation';
import { getRecommendationContent } from './CampaignRecommendations.utils';
import RecommendationModal from './RecommendationModal';

import * as styles from './CampaignRecommendations.css';

interface ICampaignRecommendationsProps {
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** Whether there were active recommendations on load */
  hasActiveRecommendationsOnLoad: boolean;
  /** Whether any recommendations were dismissed during this session */
  hasRecommendationsDismissed: boolean;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
  /** Refreshes the recommendations collection during dismiss operations to prevent component unmounting */
  onDismissRefreshCollection: () => Promise<void>;
  /** Function to mark that a recommendation was dismissed */
  onMarkRecommendationDismissed: () => void;
  /** The recommendation models */
  recommendations: IObservableArray<RecommendationModel>;
}

/**
 * Component to display the recommendations card and modal for a Tradedesk campaign
 * @param campaign - The Tradedesk campaign
 * @param hasActiveRecommendationsOnLoad - Whether there were active recommendations on load. If so, we know to breifly load the recommendatons on the report page and then transition to the "You did it" state when they're completed.
 * @param hasRecommendationsDismissed - Whether any recommendations were dismissed during this session
 * @param onMarkRecommendationDismissed - Function to mark that a recommendation was dismissed
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @param recommendations - The recommendation models
 * */
function CampaignRecommendations({
  campaign,
  hasActiveRecommendationsOnLoad,
  hasRecommendationsDismissed,
  onMarkRecommendationDismissed,
  onRefreshCollection,
  onDismissRefreshCollection,
  recommendations,
}: ICampaignRecommendationsProps): JSX.Element {
  const { t } = useTranslation();

  const { selectedRecommendation, setSelectedRecommendationId } = useSelectedRecommendation({
    recommendations,
  });
  const { opened, onOpenModal, onCloseModal, recommendationIdParam } = useModal({
    campaign,
    recommendations,
    setSelectedRecommendationId,
  });
  const {
    ref: cardRef,
    confettiPosition,
    isExploding,
    onStartConfetti,
    onConfettiComplete,
  } = useConfetti();
  const { showEmptyState, showRecommendationCard } = useCardState({
    onStartConfetti,
    recommendations,
    hasActiveRecommendationsOnLoad,
    hasRecommendationsDismissed,
  });
  const { displayedRecommendations, showCollapseButton, toggleCollapsed, collapsed } =
    useCollapseAndSort({
      campaign,
      recommendations,
      showEmptyState,
      recommendationIdParam,
    });
  const { onDismissRecommendation } = useDismissRecommendation({
    recommendations,
    onRefreshCollection: onDismissRefreshCollection,
  });

  return (
    <>
      {selectedRecommendation && (
        <RecommendationModal
          campaign={campaign}
          onClose={onCloseModal}
          onRefreshCollection={onRefreshCollection}
          opened={opened}
          recommendation={selectedRecommendation}
        />
      )}
      {/* Putting the ref on the parent since card composite compoennts don't play nicely with forward ref */}
      {showRecommendationCard && hasActiveRecommendationsOnLoad && (
        <>
          <Confetti.Container>
            {isExploding && (
              <Confetti
                confettiSource={{
                  x: confettiPosition.x,
                  y: confettiPosition.y,
                  w: 0,
                  h: 0,
                }}
                fadeFallingConfetti={true}
                onConfettiComplete={onConfettiComplete}
              />
            )}
          </Confetti.Container>

          <div data-name={'recommendations'} ref={cardRef}>
            <Card contentClassName={styles.card} width={'full'}>
              <Card.Header title={'Recommended for you'} />
              <Card.Content addVerticalGap={true}>
                {displayedRecommendations.length > 0 ? (
                  <>
                    {displayedRecommendations.map((recommendation) => {
                      const type = recommendation.get('type') as ERecommendationType;
                      const { action, icon } = getRecommendationContent(type);

                      function handleDismissRecommendation(): Promise<void> {
                        // Set the dismissal flag IMMEDIATELY before any async operations
                        onMarkRecommendationDismissed();
                        return onDismissRecommendation(recommendation.id);
                      }

                      return (
                        <Recommendation
                          description={recommendation.get('message')}
                          highlighted={collapsed}
                          key={recommendation.id}
                          onDismiss={handleDismissRecommendation}
                          suffix={<Recommendation.Icon icon={icon} />}
                          title={recommendation.get('title')}
                        >
                          <Recommendation.Button onClick={onOpenModal(type, recommendation.id)}>
                            {action}
                          </Recommendation.Button>
                        </Recommendation>
                      );
                    })}
                    {showCollapseButton && (
                      <CollapseButton
                        className={styles.collapseButton}
                        collapsed={collapsed}
                        icons={{
                          collapsedLeft: faChevronDown,
                          collapsedRight: faChevronDown,
                          expanded: faChevronUp,
                        }}
                        onClick={toggleCollapsed}
                      >
                        {collapsed ? t('View more') : t('View less')}
                      </CollapseButton>
                    )}
                  </>
                ) : (
                  <Recommendation
                    description={t('You completed all recommendations.')}
                    layout={'vertical'}
                    suffix={
                      <Recommendation.Icon bordered={true} icon={faChartMixedUpCircleCurrency} />
                    }
                    title={t('You did it!')}
                  />
                )}
              </Card.Content>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

export default observer(CampaignRecommendations);
